import { useCallback, useEffect, useMemo, useState } from "react";
import { INITIAL_USER_FORM } from "../constants.js";
import { createCologuardUser, getCologuardUsers } from "../services/api.js";

export default function useUserManagement({ enabled, accessToken, notify }) {
  const [userFilter, setUserFilter] = useState("");
  const [userState, setUserState] = useState({ loading: false, error: "", users: [], note: "" });
  const [userForm, setUserForm] = useState(INITIAL_USER_FORM);
  const [userFormState, setUserFormState] = useState({ submitting: false, error: "", success: "" });

  const loadUsers = useCallback(async () => {
    if (!enabled) return;
    setUserState((current) => ({ ...current, loading: true, error: "" }));
    try {
      const result = await getCologuardUsers({ limit: 50, accessToken });
      setUserState({ loading: false, error: "", users: result.users || [], note: result.note || "" });
    } catch (error) {
      setUserState({ loading: false, error: error.message, users: [], note: "" });
    }
  }, [accessToken, enabled]);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  const handleUserFormChange = useCallback((event) => {
    const { name, value } = event.target;
    setUserForm((current) => ({ ...current, [name]: value }));
  }, []);

  const handleUserCreate = useCallback(async (event) => {
    event.preventDefault();
    setUserFormState({ submitting: true, error: "", success: "" });
    try {
      await createCologuardUser(userForm, { accessToken });
      const success = "Invitation sent successfully.";
      setUserFormState({ submitting: false, error: "", success });
      setUserForm(INITIAL_USER_FORM);
      notify(success, "success");
      await loadUsers();
    } catch (error) {
      setUserFormState({ submitting: false, error: error.message, success: "" });
      notify(error.message, "error");
    }
  }, [accessToken, loadUsers, notify, userForm]);

  const filteredUsers = useMemo(() => {
    const query = userFilter.trim().toLowerCase();
    if (!query) return userState.users;
    return userState.users.filter((user) =>
      String(user.name || "").toLowerCase().includes(query) ||
      String(user.email || "").toLowerCase().includes(query)
    );
  }, [userFilter, userState.users]);

  return {
    userFilter,
    userState: { ...userState, users: filteredUsers },
    userForm,
    userFormState,
    setUserFilter,
    loadUsers,
    handleUserFormChange,
    handleUserCreate,
  };
}
